import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

export interface PhoneNumberOption {
  id: number
  name: string
  number: string
}

interface RawPhoneNumber {
  id: number
  name?: string | null
  number?: string | null
}

interface PhoneNumberListResponse {
  results?: RawPhoneNumber[] | null
}

interface PhoneNumberApiResult {
  ok: boolean
  data?: unknown
}

export type GetPhoneNumbersFn = (params: {
  pbx_id: number
  limit: number
}) => Promise<PhoneNumberApiResult>

export interface UsePhoneNumberOptionsParams {
  pbxId: number | null
  getPhoneNumbers: GetPhoneNumbersFn
  /**
   * Stable string that distinguishes callers whose auth/scope differs
   * (e.g. admin3's `mangoApi` vs web-app's `phoneNumberApi`). Scoping the
   * cache key by caller prevents the admin-auth and user-auth requests for
   * the same PBX from sharing a cached response.
   */
  queryKeyScope: string
  /**
   * Maximum number of phone numbers to request from the backend. Defaults
   * to 1000 to match the legacy Admin2 Select2 behavior, which also relied
   * on typeahead search rather than pagination. PBXes with more than 1000
   * numbers would need server-side search support to fully populate the
   * dropdown.
   */
  limit?: number
  staleTimeMs?: number
}

export interface UsePhoneNumberOptionsResult {
  options: PhoneNumberOption[]
  isFetching: boolean
}

/**
 * Fetches the phone numbers on a given PBX and projects them into a shape
 * suitable for the Sending Fax Number combobox in `UserForm` and
 * `PbxUserFormDialog`. Returns an empty list until `pbxId` is set.
 */
export function usePhoneNumberOptions({
  pbxId,
  getPhoneNumbers,
  queryKeyScope,
  limit = 1000,
  staleTimeMs = 60_000,
}: UsePhoneNumberOptionsParams): UsePhoneNumberOptionsResult {
  const { data, isFetching } = useQuery({
    queryKey: [
      'users-library',
      'phoneNumberOptions',
      queryKeyScope,
      pbxId,
      limit,
    ],
    queryFn: async () => {
      if (!pbxId) return null
      const response = await getPhoneNumbers({ pbx_id: pbxId, limit })
      if (!response.ok || !response.data) return null
      return response.data as PhoneNumberListResponse
    },
    enabled: pbxId != null && pbxId > 0,
    staleTime: staleTimeMs,
  })

  const options = useMemo<PhoneNumberOption[]>(() => {
    const results = data?.results ?? []
    return results
      .filter(
        (pn): pn is RawPhoneNumber & { number: string } =>
          typeof pn.number === 'string' && pn.number.length > 0,
      )
      .map((pn) => ({
        id: pn.id,
        name: pn.name ?? '',
        number: pn.number,
      }))
  }, [data])

  return { options, isFetching }
}
